import { useEffect, useState } from 'react';
import { Search, Plus, Eye, MessageSquare, AlertCircle, User, Calendar, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

export default function ComplaintsPage() {
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, open: 0, resolved: 0, pending: 0 });

  useEffect(() => { fetchComplaints(); }, []);

  const fetchComplaints = async () => {
    try {
      const response = await api.get('/api/complaints');
      const data = response.data.data || [];
      const items = Array.isArray(data) ? data : (data.items || []);
      setComplaints(items);
      setStats({
        total: items.length,
        open: items.filter(c => (c.status || '').toLowerCase().includes('open')).length,
        pending: items.filter(c => (c.status || '').toLowerCase().includes('pending')).length,
        resolved: items.filter(c => (c.status || '').toLowerCase().includes('resolv')).length
      });
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('open')) return 'bg-red-100 text-red-800';
    if (s.includes('pending')) return 'bg-yellow-100 text-yellow-800';
    if (s.includes('resolv')) return 'bg-green-100 text-green-800';
    if (s.includes('close')) return 'bg-gray-100 text-gray-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string) => {
    const p = (priority || '').toLowerCase();
    if (p.includes('high')) return 'bg-red-100 text-red-800';
    if (p.includes('medium')) return 'bg-yellow-100 text-yellow-800';
    if (p.includes('low')) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr: string) => dateStr ? new Date(dateStr).toLocaleString('ar-EG') : '-';

  const filteredComplaints = complaints.filter(c => {
    const matchesSearch = (c.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (c.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (c.trackingNumber || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (c.status || '').toLowerCase().includes(statusFilter);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              الشكاوى
            </h1>
            <p className="text-gray-600 mt-2">ادارة ومتابعة شكاوى العملاء</p>
          </div>
          <Button onClick={() => navigate('/complaints/new')} className="bg-[#1e40af] hover:bg-[#1e3a8a]">
            <Plus className="w-4 h-4 ml-2" />شكوى جديدة
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center"><AlertCircle className="w-6 h-6 text-red-600" /></div><div><p className="text-sm text-gray-600">اجمالي الشكاوى</p><p className="text-2xl font-bold">{stats.total}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center"><MessageSquare className="w-6 h-6 text-orange-600" /></div><div><p className="text-sm text-gray-600">مفتوحة</p><p className="text-2xl font-bold text-orange-700">{stats.open}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center"><Calendar className="w-6 h-6 text-yellow-600" /></div><div><p className="text-sm text-gray-600">قيد المعالجة</p><p className="text-2xl font-bold text-yellow-700">{stats.pending}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-600">تم الحل</p><p className="text-2xl font-bold text-green-700">{stats.resolved}</p></div></CardContent></Card>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="بحث..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-10 pl-4 py-2 border rounded-lg" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
                <option value="all">جميع الحالات</option>
                <option value="open">مفتوحة</option>
                <option value="pending">قيد المعالجة</option>
                <option value="resolved">تم الحل</option>
              </select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-red-50 border-b border-red-200">
            <CardTitle className="flex items-center gap-2 text-red-900">
              <AlertCircle className="w-5 h-5" />قائمة الشكاوى ({filteredComplaints.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (<div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div><p>جاري التحميل...</p></div>) :
            filteredComplaints.length === 0 ? (<div className="text-center py-12"><AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">لا توجد شكاوى</p></div>) :
            (<div className="overflow-x-auto"><table className="w-full">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-right p-3 font-semibold text-sm">العنوان</th>
                <th className="text-right p-3 font-semibold text-sm">العميل</th>
                <th className="text-right p-3 font-semibold text-sm">رقم الشحنة</th>
                <th className="text-right p-3 font-semibold text-sm">الاولوية</th>
                <th className="text-right p-3 font-semibold text-sm">الحالة</th>
                <th className="text-right p-3 font-semibold text-sm">التاريخ</th>
                <th className="text-right p-3 font-semibold text-sm">اجراءات</th>
              </tr></thead>
              <tbody>
                {filteredComplaints.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-semibold max-w-xs truncate">{c.title || c.subject || '-'}</td>
                    <td className="p-3"><div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-500" /><span>{c.customerName || '-'}</span></div></td>
                    <td className="p-3 font-mono text-sm text-purple-700">{c.trackingNumber || '-'}</td>
                    <td className="p-3"><Badge className={getPriorityColor(c.priority)}>{c.priority || 'متوسط'}</Badge></td>
                    <td className="p-3"><Badge className={getStatusColor(c.status)}>{c.status || '-'}</Badge></td>
                    <td className="p-3 text-sm text-gray-600">{formatDate(c.createdAt)}</td>
                    <td className="p-3"><Button variant="outline" size="sm"><Eye className="w-4 h-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table></div>)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
